import { useEffect, useRef, useState } from "react";
import { Camera, FileText, ImagePlus, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

export const FILE_ERROR = "Upload a photo (JPG, PNG, HEIC) or PDF under 10 MB";
export const UNREADABLE = "Couldn't read this photo. Try again in better light or enter the details manually.";
export const AI_HINT = "Filled from photo — please check";
const OK = /\.(jpe?g|png|webp|heic|heif|pdf)$/i;

export const AiHint = ({ show, testId }) => (show ? <p className="mt-1 text-[11px] font-semibold text-amber-700" data-testid={testId}>{AI_HINT}</p> : null);

export function ScanButton({ purpose, onResult, testPrefix = "scan" }) {
  const camera = useRef(null);
  const gallery = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!OK.test(file.name) || file.size > 10 * 1024 * 1024) return setError(FILE_ERROR);
    setError(""); setBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("purpose", purpose);
      const { data } = await api.post("/scans", body, { timeout: 60000 });
      if (!data.readable) setError(UNREADABLE);
      onResult(data);
    } catch (err) { setError(errMsg(err)); }
    setBusy(false);
  };
  return (
    <div className="space-y-2" data-testid={`${testPrefix}-block`}>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" onClick={() => camera.current.click()} disabled={busy} className="h-11 rounded-xl bg-neutral-900 text-white hover:bg-neutral-800" data-testid={`${testPrefix}-camera-button`}>
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Camera className="h-4 w-4 mr-2" />}{busy ? "Reading photo…" : "Scan bill / receipt"}
        </Button>
        <Button type="button" variant="outline" onClick={() => gallery.current.click()} disabled={busy} className="h-11 rounded-xl" data-testid={`${testPrefix}-gallery-button`}>
          <ImagePlus className="h-4 w-4 mr-2" />Choose photo or PDF
        </Button>
      </div>
      <input ref={camera} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid={`${testPrefix}-camera-input`} />
      <input ref={gallery} type="file" accept="image/*,application/pdf,.heic,.heif,.pdf" className="hidden" onChange={pick} data-testid={`${testPrefix}-file-input`} />
      {error && <p className="text-sm text-rose-700" role="alert" data-testid={`${testPrefix}-error`}>{error}</p>}
    </div>
  );
}

export function PhotoThumb({ photoId, size = "h-14 w-14", testId }) {
  const [src, setSrc] = useState(null);
  const [type, setType] = useState("");
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!photoId) return undefined;
    let url;
    api.get(`/photos/${photoId}`, { responseType: "blob" }).then((r) => {
      url = URL.createObjectURL(r.data);
      setType(r.data.type);
      setSrc(url);
    }).catch(() => setSrc("error"));
    return () => url && URL.revokeObjectURL(url);
  }, [photoId]);
  if (!photoId || src === "error") return null;
  const pdf = type === "application/pdf";
  const openIt = () => (pdf ? window.open(src, "_blank") : setOpen(true));
  return (
    <>
      <button type="button" onClick={openIt} className={`${size} shrink-0 overflow-hidden rounded-lg border border-border bg-secondary grid place-items-center`} data-testid={testId || `photo-thumb-${photoId}`} aria-label="Open photo">
        {!src ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : pdf ? <FileText className="h-5 w-5 text-muted-foreground" /> : <img src={src} alt="Bill photo" className="h-full w-full object-cover" />}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl bg-card rounded-2xl p-3" data-testid="photo-viewer">
          <DialogHeader className="sr-only"><DialogTitle>Photo</DialogTitle></DialogHeader>
          <img src={src} alt="Bill full size" className="max-h-[80vh] w-full object-contain rounded-xl" data-testid="photo-viewer-image" />
        </DialogContent>
      </Dialog>
    </>
  );
}

export function ScannedPhotoBar({ scan, onClear, testPrefix = "scan" }) {
  if (!scan) return null;
  return (
    <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2" data-testid={`${testPrefix}-attached`}>
      <PhotoThumb photoId={scan.photo_id} testId={`${testPrefix}-attached-thumb`} />
      <div className="text-sm flex-1 min-w-0">
        <div className="font-semibold">Photo attached</div>
        <div className="text-xs text-amber-800">{scan.readable ? `${AI_HINT}. Nothing is saved until you tap Save.` : UNREADABLE}</div>
      </div>
      <button type="button" onClick={onClear} aria-label="Remove photo" className="h-9 w-9 grid place-items-center rounded-lg hover:bg-amber-100" data-testid={`${testPrefix}-clear`}><X className="h-4 w-4" /></button>
    </div>
  );
}
